// Global
import React from 'react';

// Local
import styles from './RecipeHero.module.scss';

import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { Breadcrumb } from 'components/authorable/shared/navigation/Breadcrumb/Breadcrumb';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import { BazaarVoiceRating } from 'components/helpers/BazaarVoice';
import { ActionItem, ActionToolbar } from 'components/helpers/ActionToolbar';
import { OneTrustFilter } from 'components/helpers/OneTrust';
import formatDate, { formatDateYMD } from 'lib/utils/date-formatter';

export type RecipeHeroAttribute =
  | 'PrepTime'
  | 'TotalTime'
  | 'IngredientCount'
  | 'ServingsCount'
  | 'CaloriesPerServing'
  | 'SpiceLevel';
export interface RecipeHeroProps {
  // Image
  imageUrl: string;
  imageAlt: string;
  // Basic info
  title: string;
  pageUrl: string;

  // Timing and serving info
  prepTime: string;
  totalTime: string;
  servingNumber: string;
  caloriesPerServing: string;

  // Bazaarvoice Rating info
  contentId: string;
  ratingAndReviewContentId?: string;

  // Ingredient count
  totalIngredients: number;

  authorAttribution: string;
  contributorId: string;
  authorName: string;
  authorProfileURL: string;
  createdDate: string;
  modifiedDate: string;

  isKitchenTested: boolean;

  urlBreadcrumbs: {
    Url: string;
    Label: string;
  }[];

  locale?: string;
  dictionary?: {
    kitchenTested?: string;
    prepTime?: string;
    totalTime?: string;
    ingredients?: string;
    servings?: string;
    caloriesPerServing?: string;
    by?: string;
    published?: string;
    modifiedDate?: string;
  };

  rdpHeroSettings?: {
    hideRdpHero?: boolean;
    hideRdpHeroImage?: boolean;
    hideRdpHeroBreadcrumb?: boolean;
    hideRdpHeroBadges?: boolean;
    hideRdpHeroRatingsAndReviews?: boolean;
    hideRdpHeroAttributes?: boolean;
    hideRdpHeroByLine?: boolean;
    hideRdpHeroByAuthorSignaturePrefix?: boolean;
    hideRdpHeroAttributeList?: RecipeHeroAttribute;
    heroActionToolbarButtons?: ActionItem[];
    heroActionToolbarIdentifier?: string;
    kitchenTestedLink?: string;
    kitchenTestedLinkTarget?: string;
  };
}

export function RecipeHero(props: RecipeHeroProps) {
  const {
    imageUrl,
    imageAlt,
    title,
    prepTime,
    totalTime,
    servingNumber,
    caloriesPerServing,
    ratingAndReviewContentId,
    totalIngredients,
    rdpHeroSettings,
    isKitchenTested,
    urlBreadcrumbs,
    modifiedDate,
    createdDate,
    locale = 'en-US',
    dictionary = {},
  } = props;

  const {
    hideRdpHero,
    hideRdpHeroImage,
    hideRdpHeroBreadcrumb,
    hideRdpHeroRatingsAndReviews,
    hideRdpHeroAttributes,
    hideRdpHeroAttributeList,
    hideRdpHeroByLine,
    hideRdpHeroByAuthorSignaturePrefix,
    heroActionToolbarButtons,
    heroActionToolbarIdentifier,
    kitchenTestedLink,
    kitchenTestedLinkTarget,
  } = rdpHeroSettings || {};

  const authorName = props.authorName;
  const authorProfileURL = props.authorProfileURL;
  if (hideRdpHero) {
    return <></>;
  }

  const breadcrumb = urlBreadcrumbs?.map((breadcrumb) => ({
    url: breadcrumb.Url,
    title: breadcrumb.Label,
  }));

  // Logic to render latest date published or modified
  // Returns timestamp or null (much cleaner than NaN)
  const toTime = (date?: string) => {
    const t = date ? new Date(date).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  };

  const createdTime = toTime(createdDate);
  const modifiedTime = toTime(modifiedDate);
  // Use modified only if it exists AND is later than created
  const useModified = modifiedTime !== null && (createdTime === null || modifiedTime > createdTime);
  const dateLabel = useModified ? dictionary.modifiedDate : dictionary.published;
  const dateValue = useModified ? modifiedDate : createdDate;

  const renderRecipeAttributes = () => {
    const attributeList = [
      { name: 'PrepTime', value: prepTime, label: dictionary.prepTime },
      { name: 'TotalTime', value: totalTime, label: dictionary.totalTime },
      { name: 'IngredientCount', value: totalIngredients, label: dictionary.ingredients },
      { name: 'ServingsCount', value: servingNumber, label: dictionary.servings },
      {
        name: 'CaloriesPerServing',
        value: caloriesPerServing,
        label: dictionary.caloriesPerServing,
      },
    ];

    return attributeList
      .filter(({ name }) => !hideRdpHeroAttributeList?.includes(name))
      .map(({ name, value, label }) => (
        <li className={styles.recipeHeroPrintAttributesListItem} key={name}>
          <span className={styles.recipeHeroPrintAttributeLabel}>{label}:</span>
          <span className={styles.recipeHeroPrintAttributeValue}>{value}</span>
        </li>
      ));
  };

  return (
    <>
      <div className={`${styles.recipeCardWrapper} printHide`} aria-label={title || ''}>
        <div className={styles.recipeCard}>
          {!hideRdpHeroImage && (
            <div className={styles.recipeCardImageWrapper}>
              <EnhancedNextImage
                priority
                src={imageUrl}
                alt={imageAlt || title}
                width={1200}
                height={675}
                className={styles.recipeCardImage}
              />
            </div>
          )}
          <div className={styles.recipeCardBodyWrapper}>
            {!hideRdpHeroBreadcrumb && breadcrumb?.length > 0 && (
              <Breadcrumb
                ancestors={breadcrumb}
                isBreadcrumbForHero={true}
                suppressJsonLd={false}
              />
            )}
            <div className={styles.recipeCardBody}>
              {isKitchenTested && kitchenTestedLink && (
                <div className={styles.recipeKitchenSinkTagWraper}>
                  <CtaLink
                    url={kitchenTestedLink}
                    target={kitchenTestedLinkTarget}
                    className={styles.recipeKitchenSinkTag}
                    ctaStyle="none"
                    ctaIconSize="sm"
                    suppressNewTabIcon
                    ctaIconBefore="kitchentips"
                    ctaIconClassName={styles.recipeKitchenSinkTagIcon}
                    text={dictionary.kitchenTested}
                  />
                </div>
              )}
              <div className={styles.recipeCardHeaderTitleWrapper}>
                <h1 className={styles.recipeCardHeaderTitle}>{title}</h1>
                {!hideRdpHeroRatingsAndReviews && (
                  <OneTrustFilter
                    optanonGroups={['2', '3', '4']}
                    enablePlaceholder={false}
                    filteredComponent={
                      <BazaarVoiceRating
                        ratingAndReviewContentId={ratingAndReviewContentId}
                        showType="rating_summary"
                      />
                    }
                  />
                )}
              </div>

              {!hideRdpHeroAttributes && (
                <div className={styles.recipeCardHeaderIngredients}>
                  <div className={styles.recipeCardHeaderIngredientsList}>
                    <ul className={styles.recipeCardHeaderIngredientsListItemList}>
                      {!hideRdpHeroAttributeList?.includes('PrepTime') && prepTime && (
                        <li className={styles.recipeCardHeaderIngredientsListItemName}>
                          <div className={styles.recipeCardHeaderIngredientsListItemNameText}>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextLabel}
                            >
                              {dictionary.prepTime}:
                            </span>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextValue}
                            >
                              {prepTime}
                            </span>
                          </div>
                        </li>
                      )}
                      {!hideRdpHeroAttributeList?.includes('TotalTime') && totalTime && (
                        <li className={styles.recipeCardHeaderIngredientsListItemName}>
                          <div className={styles.recipeCardHeaderIngredientsListItemNameText}>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextLabel}
                            >
                              {dictionary.totalTime}:
                            </span>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextValue}
                            >
                              {totalTime}
                            </span>
                          </div>
                        </li>
                      )}
                      {!hideRdpHeroAttributeList?.includes('IngredientCount') &&
                        totalIngredients && (
                          <li className={styles.recipeCardHeaderIngredientsListItemName}>
                            <div className={styles.recipeCardHeaderIngredientsListItemNameText}>
                              <span
                                className={styles.recipeCardHeaderIngredientsListItemNameTextLabel}
                              >
                                {dictionary.ingredients}:
                              </span>
                              <span
                                className={styles.recipeCardHeaderIngredientsListItemNameTextValue}
                              >
                                {totalIngredients}
                              </span>
                            </div>
                          </li>
                        )}
                      {!hideRdpHeroAttributeList?.includes('ServingsCount') && servingNumber && (
                        <li className={styles.recipeCardHeaderIngredientsListItemName}>
                          <div className={styles.recipeCardHeaderIngredientsListItemNameText}>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextLabel}
                            >
                              {dictionary.servings}:
                            </span>
                            <span
                              className={styles.recipeCardHeaderIngredientsListItemNameTextValue}
                            >
                              {servingNumber}
                            </span>
                          </div>
                        </li>
                      )}
                      {!hideRdpHeroAttributeList?.includes('CaloriesPerServing') &&
                        caloriesPerServing && (
                          <li className={styles.recipeCardHeaderIngredientsListItemName}>
                            <div className={styles.recipeCardHeaderIngredientsListItemNameText}>
                              <span
                                className={styles.recipeCardHeaderIngredientsListItemNameTextLabel}
                              >
                                {dictionary.caloriesPerServing}:
                              </span>
                              <span
                                className={styles.recipeCardHeaderIngredientsListItemNameTextValue}
                              >
                                {caloriesPerServing}
                              </span>
                            </div>
                          </li>
                        )}
                    </ul>
                  </div>
                </div>
              )}

              <ActionToolbar
                className={styles.recipeControlButtons}
                actions={heroActionToolbarButtons}
                toolbarIdentifier={heroActionToolbarIdentifier}
                variant="horizontal"
              />

              {!hideRdpHeroByLine && (authorName || modifiedDate) && (
                <div className={styles.recipeByLine}>
                  <div className={styles.recipeByLinePublishDateWrapper}>
                    {authorName && (
                      <span className={styles.byWrapper}>
                        {!hideRdpHeroByAuthorSignaturePrefix && (
                          <span className={styles.byText}>{dictionary.by} </span>
                        )}
                        {authorProfileURL ? (
                          <CtaLink
                            url={authorProfileURL}
                            className={styles.authorNameLink}
                            target="_blank"
                            ctaStyle="none"
                            text={authorName}
                            suppressNewTabIcon
                          />
                        ) : (
                          <span className={styles.authorNameText}>{authorName}</span>
                        )}
                      </span>
                    )}
                    {authorName && dateValue && (
                      <span className={styles.modifiedText}>
                        {dateLabel}{' '}
                        <time dateTime={formatDateYMD(dateValue)}>
                          {formatDate(dateValue, locale, 'SHORT')}
                        </time>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recipe Hero Print ONLY */}
      <div className={styles.recipeHeroPrint}>
        <div className={styles.recipeHeroPrintInner}>
          <div className={styles.recipeHeroPrintUpper}>
            <div className={styles.recipeHeroPrintUpperLeft}>
              <h1 className={styles.recipeHeroPrintTitle}>{title}</h1>
            </div>
            <div className={styles.recipeHeroPrintUpperRight}>
              {!hideRdpHeroImage && (
                <EnhancedNextImage
                  priority
                  src={imageUrl}
                  alt={imageAlt}
                  width={1200}
                  height={675}
                  className={styles.recipeHeroPrintImage}
                />
              )}
            </div>
          </div>
          <div className={styles.recipeHeroPrintLower}>
            <ul className={styles.recipeHeroPrintAttributesList}>{renderRecipeAttributes()}</ul>
          </div>
        </div>
      </div>
    </>
  );
}
